import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { homeApi } from '@/utils/api';
import { getCached, setCached, TTL } from '@/utils/cache';
import { GramIcon } from '@/components/ui/GramIcon';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { useTranslation } from 'react-i18next';

interface TopSeller {
    id: string;
    name: string;
    username: string;
    avatar: string;
    count: number;
    value: number;
}

type Side = 'sell' | 'buy';

interface TopSellersProps {
    onNavigate?: (page: string, category?: any) => void;
}

// Modern blue check badge (white check, clean SVG)
const CheckBadge = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-2 h-2" aria-hidden="true">
        <circle cx="12" cy="12" r="12" fill="#3b82f6" />
        <path
            d="M7 12.2l3.1 3.1L17 8.6"
            stroke="#ffffff"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

export const TopSellers: React.FC<TopSellersProps> = ({ onNavigate }) => {
    const { impactOccurred } = useHapticFeedback();
    const { t } = useTranslation('home');
    const { t: tc } = useTranslation('common');
    const [side, setSide] = useState<Side>('sell');
    const [sellersMap, setSellersMap] = useState<Record<Side, TopSeller[]>>({
        sell: getCached<TopSeller[]>('top_sellers', TTL.HOME) || [],
        buy: getCached<TopSeller[]>('top_buyers', TTL.HOME) || [],
    });
    const [loading, setLoading] = useState(false);

    const sellers = sellersMap[side] || [];

    const scrollRef = useRef<HTMLDivElement>(null);
    const dragState = useRef<{ active: boolean; startX: number; startScroll: number; moved: boolean }>({
        active: false, startX: 0, startScroll: 0, moved: false
    });

    // Fetch + cache (stale-while-revalidate), keyed by side
    useEffect(() => {
        const tgUser: any = window.Telegram?.WebApp?.initDataUnsafe?.user || {};
        const userId = tgUser?.id;
        const cacheKey = side === 'buy' ? 'top_buyers' : 'top_sellers';

        setLoading(true);
        homeApi.getTopSellers(userId, side)
            .then(r => r.json())
            .then(data => {
                const list = Array.isArray(data) ? data : (data?.sellers || []);
                if (list.length > 0) {
                    setSellersMap(prev => ({ ...prev, [side]: list }));
                    setCached(cacheKey, list);
                }
            })
            .catch(() => {})
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [side]);

    const handleSideChange = (next: Side) => {
        if (next === side) return;
        impactOccurred?.('medium');
        setSide(next);
    };

    const scrollBy = useCallback((dir: 1 | -1) => {
        const el = scrollRef.current;
        if (!el) return;
        const step = Math.max(el.clientWidth * 0.7, 160);
        el.scrollBy({ left: dir * step, behavior: 'smooth' });
    }, []);

    // Drag-to-scroll (mouse + touch)
    const onPointerDown = (e: React.PointerEvent) => {
        const el = scrollRef.current;
        if (!el) return;
        dragState.current = { active: true, startX: e.clientX, startScroll: el.scrollLeft, moved: false };
        el.setPointerCapture(e.pointerId);
    };
    const onPointerMove = (e: React.PointerEvent) => {
        const el = scrollRef.current;
        if (!el || !dragState.current.active) return;
        const dx = e.clientX - dragState.current.startX;
        if (Math.abs(dx) > 4) dragState.current.moved = true;
        el.scrollLeft = dragState.current.startScroll - dx;
    };
    const onPointerUp = () => {
        dragState.current.active = false;
        if (dragState.current.moved) impactOccurred?.('light');
    };

    return (
        <div className="mt-4 select-none">
            {/* Section Header: capsule Sell/Buy switch + scroll controls */}
            <div className="flex items-center justify-between px-4 mb-3">
                <div className="flex items-center gap-2">
                    <span className="text-[13px] font-black italic uppercase tracking-tighter text-tg-text">{tc('top')}</span>
                    <div className="bg-white/[0.07] border border-white/10 rounded-full p-0.5 flex gap-0.5 backdrop-blur-sm">
                        <button
                            type="button"
                            onClick={() => handleSideChange('sell')}
                            className={`px-3 py-[3px] rounded-full text-[10px] font-black uppercase tracking-tighter transition-all duration-300 ${
                                side === 'sell'
                                    ? 'bg-gradient-to-r from-[#3390EC] to-[#007AFF] text-white shadow-md shadow-[#3390EC]/30'
                                    : 'text-tg-hint active:scale-95'
                            }`}
                        >
                            {tc('sell')}
                        </button>
                        <button
                            type="button"
                            onClick={() => handleSideChange('buy')}
                            className={`px-3 py-[3px] rounded-full text-[10px] font-black uppercase tracking-tighter transition-all duration-300 ${
                                side === 'buy'
                                    ? 'bg-gradient-to-r from-[#3390EC] to-[#007AFF] text-white shadow-md shadow-[#3390EC]/30'
                                    : 'text-tg-hint active:scale-95'
                            }`}
                        >
                            {tc('buy')}
                        </button>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        aria-label={t('scrollLeft')}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={() => { impactOccurred?.('light'); scrollBy(-1); }}
                        className="glass-chip w-8 h-8 rounded-full flex items-center justify-center text-tg-text active:scale-95 transition-all"
                    >
                        <ChevronLeft size={16} className="rtl-flip" />
                    </button>
                    <button
                        type="button"
                        aria-label={t('scrollRight')}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={() => { impactOccurred?.('light'); scrollBy(1); }}
                        className="glass-chip w-8 h-8 rounded-full flex items-center justify-center text-tg-text active:scale-95 transition-all"
                    >
                        <ChevronRight size={16} className="rtl-flip" />
                    </button>
                </div>
            </div>

            {/* Horizontal Cards */}
            <div
                ref={scrollRef}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerLeave={onPointerUp}
                className="no-scrollbar flex gap-3 overflow-x-auto px-4 pb-1 cursor-grab active:cursor-grabbing"
                style={{ touchAction: 'pan-y' }}
            >
                {sellers.map((s) => (
                    <div
                        key={s.id}
                        onPointerUp={(e) => {
                            if (dragState.current.moved) { e.stopPropagation(); return; }
                        }}
                        onClick={() => {
                            if (dragState.current.moved) return;
                            impactOccurred?.('medium');
                            onNavigate?.('user_usernames', {
                                id: s.id,
                                name: s.name,
                                username: s.username,
                                avatar: s.avatar,
                                count: s.count,
                                value: s.value
                            });
                        }}
                        className="flex-shrink-0 w-12 group"
                    >
                        {/* Square tile with rounded corners + blue check badge */}
                        <div className="relative w-12 h-12 rounded-[12px] bg-gradient-to-br from-zinc-800 via-zinc-900 to-black border border-white/10 overflow-hidden shadow group-active:scale-95 transition-transform">
                            {s.avatar ? (
                                <img
                                    src={s.avatar}
                                    alt={s.name}
                                    className="w-full h-full object-cover"
                                    onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/icons/default-avatar.svg'; }}
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-[10px] font-black italic text-white/20">
                                    {(s.name || 'S').charAt(0)}
                                </div>
                            )}
                            <div className="absolute bottom-0.5 right-0.5">
                                <CheckBadge />
                            </div>
                        </div>

                        {/* Soft name */}
                        <p className="mt-1 text-[7px] font-semibold text-tg-text/90 truncate text-center leading-tight">
                            {s.name}
                        </p>

                        {/* Username count value + GRAM */}
                        <div className="flex items-center justify-center gap-0.5 mt-0.5">
                            <GramIcon className="w-2 h-2 opacity-90" />
                            <span className="text-[8px] font-black text-tg-text leading-none">
                                {Number(s.value).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            </span>
                        </div>
                    </div>
                ))}

                {loading && (
                    <div className="flex-shrink-0 w-12 flex flex-col items-center gap-2 py-3">
                        <div className="w-5 h-5 border-2 border-tg-text/10 border-t-tg-text/40 rounded-full animate-spin" />
                    </div>
                )}
            </div>
        </div>
    );
};

export default TopSellers;